<template>
  <el-dialog
    title="AI Generate Questions"
    :visible.sync="dialogVisible"
    width="800px"
    :close-on-click-modal="false"
    append-to-body
    @close="handleClose"
  >
    <!-- Step 1: Configuration -->
    <div v-if="step === 1" class="generator-config">
      <el-form
        ref="configForm"
        :model="config"
        :rules="configRules"
        label-position="top"
      >
        <el-form-item label="Content Scope" prop="scope">
          <TestBankScopeSelector
            :collection-form="collectionForm"
            v-model="config.scope"
          />
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="Number of Questions" prop="count">
              <el-input-number
                v-model="config.count"
                :min="1"
                :max="100"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Difficulty" prop="difficulty">
              <el-select v-model="config.difficulty" style="width: 100%;">
                <el-option label="Easy" value="easy" />
                <el-option label="Medium" value="medium" />
                <el-option label="Hard" value="hard" />
                <el-option label="Mixed" value="mixed" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="Language">
              <el-select v-model="config.language" style="width: 100%;">
                <el-option label="English" value="en" />
                <el-option label="Chinese" value="zh" />
                <el-option label="Match Content" value="auto" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-form-item label="Additional Instructions (Optional)">
          <el-input
            type="textarea"
            v-model="config.instructions"
            placeholder="E.g., Focus on vocabulary, include comprehension questions..."
            :autosize="{ minRows: 2, maxRows: 4 }"
          />
        </el-form-item>
      </el-form>
    </div>

    <!-- Step 2: Preview & Edit -->
    <div v-else-if="step === 2" class="generator-preview">
      <div class="preview-header">
        <span class="preview-count">{{ generatedQuestions.length }} questions generated</span>
        <el-button size="small" @click="step = 1">
          <i class="el-icon-back"></i> Back to Config
        </el-button>
      </div>

      <div class="preview-list" v-loading="generating">
        <div
          v-for="(question, index) in generatedQuestions"
          :key="index"
          class="preview-item"
        >
          <div class="preview-item-header">
            <span class="question-number">Q{{ index + 1 }}</span>
            <el-tag :type="difficultyTagType(question.difficulty)" size="mini">
              {{ question.difficulty || 'medium' }}
            </el-tag>
            <div class="preview-item-actions">
              <el-button
                type="text"
                icon="el-icon-edit"
                size="small"
                @click="editQuestion(index)"
              >Edit</el-button>
              <el-button
                type="text"
                icon="el-icon-delete"
                size="small"
                class="delete-btn"
                @click="removeQuestion(index)"
              >Remove</el-button>
            </div>
          </div>

          <div class="preview-question">{{ question.question }}</div>

          <div class="preview-options">
            <div
              v-for="(option, optIndex) in question.options"
              :key="optIndex"
              class="preview-option"
              :class="{ 'correct': optionLabels[optIndex] === question.correct_answer }"
            >
              <span class="option-label">{{ optionLabels[optIndex] }}.</span>
              <span>{{ option }}</span>
              <i v-if="optionLabels[optIndex] === question.correct_answer" class="el-icon-check"></i>
            </div>
          </div>

          <div v-if="question.explanation" class="preview-explanation">
            <strong>Explanation:</strong> {{ question.explanation }}
          </div>
        </div>

        <div v-if="generatedQuestions.length === 0" class="empty-preview">
          <i class="el-icon-warning"></i>
          <p>No questions generated. Try adjusting your settings.</p>
        </div>
      </div>
    </div>

    <!-- Edit Question Dialog -->
    <el-dialog
      title="Edit Question"
      :visible.sync="editDialogVisible"
      width="600px"
      append-to-body
    >
      <el-form v-if="editingQuestion" label-position="top">
        <el-form-item label="Question">
          <el-input
            type="textarea"
            v-model="editingQuestion.question"
            :autosize="{ minRows: 2, maxRows: 4 }"
          />
        </el-form-item>

        <el-form-item label="Options">
          <div
            v-for="(option, index) in editingQuestion.options"
            :key="index"
            style="margin-bottom: 8px;"
          >
            <el-input v-model="editingQuestion.options[index]">
              <template slot="prepend">{{ optionLabels[index] }}.</template>
            </el-input>
          </div>
        </el-form-item>

        <el-form-item label="Correct Answer">
          <el-radio-group v-model="editingQuestion.correct_answer">
            <el-radio-button
              v-for="(option, index) in editingQuestion.options"
              :key="index"
              :label="optionLabels[index]"
            >{{ optionLabels[index] }}</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <el-form-item label="Explanation">
          <el-input
            type="textarea"
            v-model="editingQuestion.explanation"
            :autosize="{ minRows: 2, maxRows: 4 }"
          />
        </el-form-item>

        <el-form-item label="Difficulty">
          <el-select v-model="editingQuestion.difficulty">
            <el-option label="Easy" value="easy" />
            <el-option label="Medium" value="medium" />
            <el-option label="Hard" value="hard" />
          </el-select>
        </el-form-item>
      </el-form>

      <span slot="footer">
        <el-button @click="editDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="saveEditedQuestion">Save</el-button>
      </span>
    </el-dialog>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <span v-if="generatingProgress" class="generating-progress">{{ generatingProgress }}</span>
      <el-button
        v-if="step === 1"
        type="primary"
        @click="generateQuestions"
        :loading="generating"
      >
        <i class="el-icon-magic-stick"></i> Generate
      </el-button>
      <el-button
        v-else
        type="primary"
        @click="saveQuestions"
        :loading="saving"
        :disabled="generatedQuestions.length === 0"
      >
        <i class="el-icon-check"></i> Save {{ generatedQuestions.length }} Questions
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import { postRequest } from '@/apis/front'
import TestBankScopeSelector from './TestBankScopeSelector.vue'

export default {
  name: 'TestBankAIGenerator',
  components: {
    TestBankScopeSelector
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    collectionId: {
      type: String,
      required: true
    },
    collectionForm: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dialogVisible: false,
      step: 1,
      generating: false,
      saving: false,
      optionLabels: ['A', 'B', 'C', 'D'],
      config: {
        scope: {
          type: 'collection', // 'collection', 'groups', 'knowits'
          selectedGroups: [],
          selectedKnowits: []
        },
        count: 5,
        difficulty: 'mixed',
        language: 'auto',
        instructions: ''
      },
      configRules: {
        count: [
          { required: true, message: 'Please specify number of questions', trigger: 'change' }
        ]
      },
      generatedQuestions: [],
      generatingProgress: '',
      editDialogVisible: false,
      editingQuestion: null,
      editingIndex: -1
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.reset()
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    reset() {
      this.step = 1
      this.generatedQuestions = []
      this.config = {
        scope: {
          type: 'collection',
          selectedGroups: [],
          selectedKnowits: []
        },
        count: 5,
        difficulty: 'mixed',
        language: 'auto',
        instructions: ''
      }
    },

    handleClose() {
      this.dialogVisible = false
      this.reset()
    },

    difficultyTagType(difficulty) {
      const types = {
        easy: 'success',
        medium: 'warning',
        hard: 'danger'
      }
      return types[difficulty] || 'info'
    },

    extractContentFromScope() {
      const content = []
      const groups = this.collectionForm.groups || []

      if (this.config.scope.type === 'collection') {
        groups.forEach((group, groupIndex) => {
          const groupContent = {
            groupName: group.title,
            items: []
          }
          const knowits = group.knowits || []
          knowits.forEach(knowit => {
            const postInfo = knowit.post_info || {}
            groupContent.items.push({
              title: postInfo.title,
              content: this.extractKnowitContent(postInfo)
            })
          })
          content.push(groupContent)
        })
      } else if (this.config.scope.type === 'groups') {
        this.config.scope.selectedGroups.forEach(groupIndex => {
          const group = groups[groupIndex]
          if (group) {
            const groupContent = {
              groupName: group.title,
              items: []
            }
            const knowits = group.knowits || []
            knowits.forEach(knowit => {
              const postInfo = knowit.post_info || {}
              groupContent.items.push({
                title: postInfo.title,
                content: this.extractKnowitContent(postInfo)
              })
            })
            content.push(groupContent)
          }
        })
      } else if (this.config.scope.type === 'knowits') {
        this.config.scope.selectedKnowits.forEach(({ groupIndex, knowitIndex }) => {
          const group = groups[groupIndex]
          if (group) {
            const knowit = (group.knowits || [])[knowitIndex]
            if (knowit) {
              const postInfo = knowit.post_info || {}
              content.push({
                groupName: group.title,
                items: [{
                  title: postInfo.title,
                  content: this.extractKnowitContent(postInfo)
                }]
              })
            }
          }
        })
      }

      return content
    },

    extractKnowitContent(postInfo) {
      const texts = []
      const answers = postInfo.answers || []
      answers.forEach(answer => {
        if (answer.content_type === 'text' && answer.content) {
          texts.push(answer.content)
        } else if (answer.content_type === 'quiz' && answer.quiz_content) {
          texts.push(`Question: ${answer.quiz_content.question}`)
        }
      })
      return texts.join('\n')
    },

    async generateQuestions() {
      this.generating = true
      this.generatingProgress = ''
      try {
        const scopeContent = this.extractContentFromScope()

        if (scopeContent.length === 0 || scopeContent.every(g => g.items.length === 0)) {
          this.$message.warning('No content found in selected scope')
          this.generating = false
          return
        }

        const totalCount = this.config.count
        const batchSize = 15
        const allQuestions = []

        if (totalCount <= batchSize) {
          // Single batch — no progress needed
          const response = await postRequest('generateTestBankQuestions', {
            collection_id: this.collectionId,
            user_id: this.$store.state.user_id,
            scope_content: scopeContent,
            count: totalCount,
            difficulty: this.config.difficulty,
            language: this.config.language,
            instructions: this.config.instructions
          })
          if (response.data.code === 0) {
            allQuestions.push(...(response.data.data.questions || []))
          } else {
            this.$message.error(response.data.msg || 'Failed to generate questions')
            return
          }
        } else {
          // Multiple batches
          const totalBatches = Math.ceil(totalCount / batchSize)
          let remaining = totalCount
          for (let batch = 0; batch < totalBatches; batch++) {
            const batchCount = Math.min(batchSize, remaining)
            this.generatingProgress = `Generating batch ${batch + 1}/${totalBatches} (${allQuestions.length}/${totalCount} questions)...`

            try {
              const response = await postRequest('generateTestBankQuestions', {
                collection_id: this.collectionId,
                user_id: this.$store.state.user_id,
                scope_content: scopeContent,
                count: batchCount,
                difficulty: this.config.difficulty,
                language: this.config.language,
                instructions: this.config.instructions,
                batch_index: batch,
                total_batches: totalBatches
              })
              if (response.data.code === 0) {
                allQuestions.push(...(response.data.data.questions || []))
              }
            } catch (batchError) {
              console.error(`Batch ${batch + 1} failed:`, batchError)
            }

            remaining -= batchCount
          }
        }

        this.generatedQuestions = allQuestions
        this.step = 2
        if (this.generatedQuestions.length === 0) {
          this.$message.warning('AI could not generate questions from the content')
        } else {
          this.$message.success(`${this.generatedQuestions.length} questions generated`)
        }
      } catch (error) {
        console.error('Error generating questions:', error)
        this.$message.error('Failed to generate questions')
      } finally {
        this.generating = false
        this.generatingProgress = ''
      }
    },

    editQuestion(index) {
      this.editingIndex = index
      this.editingQuestion = JSON.parse(JSON.stringify(this.generatedQuestions[index]))
      // Ensure 4 options
      while (this.editingQuestion.options.length < 4) {
        this.editingQuestion.options.push('')
      }
      this.editDialogVisible = true
    },

    saveEditedQuestion() {
      if (this.editingIndex >= 0) {
        this.$set(this.generatedQuestions, this.editingIndex, this.editingQuestion)
      }
      this.editDialogVisible = false
      this.editingQuestion = null
      this.editingIndex = -1
    },

    removeQuestion(index) {
      this.generatedQuestions.splice(index, 1)
    },

    async saveQuestions() {
      if (this.generatedQuestions.length === 0) {
        return
      }

      this.saving = true
      try {
        const response = await postRequest('saveGeneratedTestBankQuestions', {
          collection_id: this.collectionId,
          user_id: this.$store.state.user_id,
          questions: this.generatedQuestions
        })

        if (response.data.code === 0) {
          this.$message.success(`${this.generatedQuestions.length} questions saved`)
          this.$emit('generated')
          this.handleClose()
        } else {
          this.$message.error(response.data.msg || 'Failed to save questions')
        }
      } catch (error) {
        console.error('Error saving questions:', error)
        this.$message.error('Failed to save questions')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.generator-config {
  padding: 10px 0;
}

.generator-preview {
  max-height: 500px;
  overflow-y: auto;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #ebeef5;
}

.preview-count {
  font-weight: 600;
  color: #303133;
}

.preview-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.preview-item {
  padding: 16px;
  background: #fafafa;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}

.preview-item-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.question-number {
  font-weight: 600;
  color: #409eff;
}

.preview-item-actions {
  margin-left: auto;
}

.preview-item-actions .delete-btn {
  color: #f56c6c;
}

.preview-question {
  font-size: 15px;
  color: #303133;
  margin-bottom: 12px;
  line-height: 1.6;
}

.preview-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
}

.preview-option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  background: #fff;
  border-radius: 4px;
  font-size: 14px;
}

.preview-option.correct {
  background: #e1f3d8;
  border: 1px solid #67c23a;
}

.preview-option .option-label {
  font-weight: 600;
  color: #606266;
}

.preview-option .el-icon-check {
  color: #67c23a;
  margin-left: auto;
}

.preview-explanation {
  padding: 10px;
  background: #fdf6ec;
  border-radius: 4px;
  font-size: 13px;
  color: #606266;
}

.preview-explanation strong {
  color: #e6a23c;
}

.empty-preview {
  text-align: center;
  padding: 40px;
  color: #909399;
}

.empty-preview i {
  font-size: 32px;
  margin-bottom: 10px;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
}

.generating-progress {
  font-size: 13px;
  color: #909399;
  margin-right: 8px;
}
</style>
